import React from 'react';
import { env } from '@/config/env';
import type {
    CatalogParseProgress,
    CatalogWorkbookRefusal,
} from './catalogImportParse';
import type {
    CatalogImportParsedFile,
    CatalogImportWorkerReply,
    CatalogImportWorkerRequest,
} from './catalogImportWorkerProtocol';

export type CatalogImportParserStatus =
    'idle' | 'parsing' | 'done' | 'refused' | 'failed';

/** How one parse ended. */
export type CatalogImportParseOutcome =
    | { status: 'done'; parsed: CatalogImportParsedFile }
    | { status: 'refused'; refusal: CatalogWorkbookRefusal }
    /** The worker could not start, crashed, or the parse threw. */
    | { status: 'failed' }
    /** Cancelled, replaced by another parse, or its owner unmounted. */
    | { status: 'cancelled' };

export type CatalogImportParseInput = {
    /** The file's bytes; transferred to the worker, so detached afterwards. */
    buffer: ArrayBuffer;
    attributeTypeNames: readonly string[];
};

export type UseCatalogImportParserResult = {
    status: CatalogImportParserStatus;
    /** Rows mapped so far; `null` until the worker's first report. */
    progress: CatalogParseProgress | null;
    /** The parsed file (`done`) or the refusal (`refused`); else `null`. */
    result: CatalogImportParsedFile | CatalogWorkbookRefusal | null;
    /**
     * Parses one file in a fresh worker and resolves with how it ended. A
     * parse already running is cancelled first.
     */
    start: (
        input: CatalogImportParseInput,
    ) => Promise<CatalogImportParseOutcome>;
    /** Stops the running parse: its `start` resolves `cancelled`. */
    cancel: () => void;
};

type Running = {
    worker: Worker;
    settle: (outcome: CatalogImportParseOutcome) => void;
};

type ParserState = Pick<
    UseCatalogImportParserResult,
    'status' | 'progress' | 'result'
>;

const IDLE: ParserState = { status: 'idle', progress: null, result: null };

/**
 * Runs the catalog-import parse in a module Web Worker
 * (`catalogImportWorker.ts`, served at `env.catalogImportWorkerUrl`), so the
 * tab stays responsive while a 2,000-row file is read.
 *
 * One worker per parse, terminated as soon as the parse ends, is cancelled,
 * or the owner unmounts: exceljs's memory goes with it. The buffer is
 * transferred, not copied.
 */
export const useCatalogImportParser = (): UseCatalogImportParserResult => {
    const [state, setState] = React.useState<ParserState>(IDLE);
    const running = React.useRef<Running | null>(null);

    /** Ends the running parse, if any, with `outcome`. */
    const stop = React.useCallback((outcome: CatalogImportParseOutcome) => {
        const current = running.current;
        if (!current) {
            return;
        }
        running.current = null;
        current.worker.terminate();
        current.settle(outcome);
    }, []);

    const cancel = React.useCallback(() => {
        const current = running.current;
        if (!current) {
            return;
        }
        const message: CatalogImportWorkerRequest = { type: 'cancel' };
        current.worker.postMessage(message);
        stop({ status: 'cancelled' });
        setState(IDLE);
    }, [stop]);

    const start = React.useCallback(
        ({ buffer, attributeTypeNames }: CatalogImportParseInput) => {
            stop({ status: 'cancelled' });

            return new Promise<CatalogImportParseOutcome>((resolve) => {
                const fail = () => {
                    stop({ status: 'failed' });
                    setState({
                        status: 'failed',
                        progress: null,
                        result: null,
                    });
                };

                let worker: Worker;
                try {
                    worker = new Worker(env.catalogImportWorkerUrl, {
                        type: 'module',
                        name: 'catalog-import-parse',
                    });
                } catch (error) {
                    console.error(
                        '[catalog import] the parse worker did not start:',
                        error,
                    );
                    setState({
                        status: 'failed',
                        progress: null,
                        result: null,
                    });
                    resolve({ status: 'failed' });
                    return;
                }

                const entry: Running = { worker, settle: resolve };
                running.current = entry;
                setState({ status: 'parsing', progress: null, result: null });

                worker.addEventListener(
                    'message',
                    (event: MessageEvent<CatalogImportWorkerReply>) => {
                        if (running.current !== entry) {
                            return;
                        }
                        const reply = event.data;
                        switch (reply.type) {
                            case 'progress':
                                setState({
                                    status: 'parsing',
                                    progress: {
                                        parsedRows: reply.parsedRows,
                                        totalRows: reply.totalRows,
                                    },
                                    result: null,
                                });
                                return;
                            case 'done':
                                stop({ status: 'done', parsed: reply.parsed });
                                setState({
                                    status: 'done',
                                    progress: null,
                                    result: reply.parsed,
                                });
                                return;
                            case 'refused':
                                stop({
                                    status: 'refused',
                                    refusal: reply.refusal,
                                });
                                setState({
                                    status: 'refused',
                                    progress: null,
                                    result: reply.refusal,
                                });
                                return;
                            case 'failed':
                                console.error(
                                    '[catalog import] the parse failed:',
                                    reply.message,
                                );
                                fail();
                                return;
                            case 'cancelled':
                                stop({ status: 'cancelled' });
                                setState(IDLE);
                                return;
                        }
                    },
                );
                // A script that does not load, an uncaught throw, or a reply
                // that cannot be read: the parse is lost either way.
                const onBroken = (event: Event) => {
                    if (running.current !== entry) {
                        return;
                    }
                    console.error(
                        '[catalog import] the parse worker broke:',
                        event,
                    );
                    fail();
                };
                worker.addEventListener('error', onBroken);
                worker.addEventListener('messageerror', onBroken);

                const message: CatalogImportWorkerRequest = {
                    type: 'start',
                    buffer,
                    attributeTypeNames,
                };
                worker.postMessage(message, [buffer]);
            });
        },
        [stop],
    );

    // Unmounting stops the parse: its `start` resolves `cancelled`.
    React.useEffect(() => () => stop({ status: 'cancelled' }), [stop]);

    return { ...state, start, cancel };
};
